<template>
	<core-layout-content full>
		<core-layout-headline-and-actions>
			<template slot="headline">
				{{ $t("case_management") }}
				<core-pagination-info
					:itemsPerPage="itemsPerPage"
					:numberOfItems="listing.numberOfItems"
					:currentPage="computedPage"
				/>
			</template>
			<template slot="actions">
				<j-button
					v-if="
						![
							'hkecic',
							'hkecic_uat',
							'fiLife_uat',
							'fiLife',
							'bupa_uat',
							'apeiron_uat',
							'apeiron'
						].includes($store.state.tenantId)
					"
					v-can.and="['writeCases', 'writeProposals']"
					:to="{ name: 'CaseManagementCreate' }"
				>
					<crm-icon name="add" size="small" color="c5" />
					{{ $t("create") }}
				</j-button>

				<!-- TEMPORARY HACK -->
				<!-- For hkecic, demo_2 and fiLife we don't have the component in the cms, so create directly the case -->
				<j-button
					v-if="
						[
							'hkecic',
							'hkecic_uat',
							'fiLife_uat',
							'fiLife',
							'bupa_uat',
							'apeiron_uat',
							'apeiron'
						].includes($store.state.tenantId)
					"
					v-can.and="['writeCases', 'writeProposals']"
					@click="createEmptyCase"
				>
					<crm-icon name="add" size="small" color="c5" />
					{{ $t("create") }}
				</j-button>
			</template>
		</core-layout-headline-and-actions>

		<!-- Search -->
		<div class="flex wrap xs-12 xs-left mb2">
			<div class="flex xs-12">
				<j-base-input-search
					:placeholder="$t('search_proposal_or_case_number_or_holder_name')"
					:value="searchTerm"
					@input="onSearch($event)"
				/>
				<j-button
					class="ml2"
					type="tertiary"
					v-if="searchTerm"
					@click="cancelSearch"
					>{{ $t("cancel") }}</j-button
				>
			</div>
		</div>

		<!-- Data Container -->
		<j-card class="bg5">
			<j-data-table
				:header="header"
				:list="computedList"
				:sortable="false"
				:isLoading="isLoading"
				@sort="onSort($event)"
			>
				<template slot="list" slot-scope="{ item }">
					<td v-for="(column, i) in header" :key="i">
						<template>
							<!-- Name -->
							<template v-if="column.field === 'clientName'">
								<router-link
									:to="{
										name: `CaseManagementDetail`,
										params: { id: item.id }
									}"
									>{{ item.clientName ? item.clientName : "---" }}</router-link
								>
							</template>

							<!-- status -->
							<template v-else-if="column.field === 'status'">
								<j-tag
									:label="item.status"
									:variant="decideStatusClass(item.originalStatus)"
								/>
							</template>

							<!-- Others -->
							<template v-else>{{ item[column.field] }}</template>

							<!-- Actions Field -->
							<template v-if="column.field === 'actions'">
								<div class="action">
									<!-- Delete -->
									<crm-icon
										name="delete"
										size="normal"
										color="cError"
										@click.native="showModal(item.id)"
										v-can="'writeCases'"
									/>
								</div>
							</template>
						</template>
					</td>
				</template>
				<template slot="empty">{{ $t("errors.no_data") }}</template>
			</j-data-table>
		</j-card>

		<!-- Pagination -->
		<div class="flex xs-12 p4 xs-center mb15">
			<j-pagination
				:amount="listing.numberOfItems ? listing.numberOfItems : 0"
				:perPage="itemsPerPage"
				:activePage="computedPage"
				@click="onPageChange($event)"
			/>
		</div>

		<!-- Delete confirmation modal -->
		<j-dialog
			:height="200"
			:width="600"
			:title="$t('delete')"
			type="delete"
			@confirm="removeCase(selectedItemId)"
			>{{ $t("errors.are_you_sure_you_want_to_delete_case") }}</j-dialog
		>
	</core-layout-content>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import JTag from "@/components/JTag/"
import JBaseInputSearch from "@/components/JBaseInputSearch/"
import JPagination from "@/components/JPagination/"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import JDialog from "@/components/JDialog/"
import dayjs from "dayjs"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import debounce from "lodash.debounce"

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDataTable,
		JTag,
		JBaseInputSearch,
		JPagination,
		CorePaginationInfo,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		JDialog
	},

	data() {
		return {
			isLoading: false,

			// Actual fetched data
			listing: {
				numberOfItems: 0,
				list: []
			},

			// Search term
			searchTerm: null,

			// Pagination
			itemsPerPage: 30,

			// Header of table
			header: [
				// {
				// 	field: "id",
				// 	text: this.$t("id")
				// },
				// {
				// 	field: "caseNumber",
				// 	text: this.$t("case_number")
				// },
				{
					field: "clientName",
					text: this.$t("client")
				},
				{
					field: "componentName",
					text: this.$t("template")
				},
				{
					text: this.$t("created"),
					field: "createdAt"
				},
				{
					text: this.$t("actions"),
					field: "actions"
				}
			],

			selectedItemId: null
		}
	},

	computed: {
		/**
		 * Compute fetch data for data table
		 * This is what we are gonna display
		 */
		computedList() {
			if (!this.listing.list) return []

			return this.listing.list.map(item => ({
				id: item.id,
				caseNumber: item.caseNumber,
				clientName: item?.holder?.name || "---",
				componentName: item?.component?.name,
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(
							this.$store.state.settings.dateTimeFormat
					  )
					: null
			}))
		},

		computedPage() {
			return this.$store.state.caseManagement.listing.page
		},

		computedWhere() {
			if (!this.searchTerm) return

			return {
				or: [
					{
						proposal: {
							// proposalWhere
							proposalNumber_contains: this.searchTerm
						}
					},
					{
						proposal: {
							policyId_contains: this.searchTerm
						}
					},
					{
						proposal: {
							offers_contains: {
								offerNumber: this.searchTerm
							}
						}
					},
					{
						caseNumber: this.searchTerm
					},
					{
						holderIndividual: {
							name_contains: this.searchTerm
						}
					},
					{
						holderCompany: {
							name_contains: this.searchTerm
						}
					}
				]
			}
		}
	},

	methods: {
		// HACK
		// For hkecic, we don't go to CaseManagementCreate because the case component is not in the CMS
		async createEmptyCase() {
			const query = gql`
				mutation createCase($input: createCaseInput!) {
					createCase(input: $input) {
						errors
						status
						createdStatus {
							id
						}
					}
				}
			`

			this.isLoading = true
			const res = await api({
				query,
				variables: { input: {} }
			})
			this.isLoading = false

			// Don't check errors, just redirect to detail page
			this.$router.push({
				name: "CaseManagementDetail",
				params: { id: res?.data?.createCase?.createdStatus?.id }
			})
		},

		onPageChange(pageIndex) {
			this.$store.commit("caseManagement/SET_PAGE", pageIndex)
			this.readAll()
			this.scrollToTop()
		},

		scrollToTop() {
			this.$scrollTo("body", 300)
		},

		onSearch: debounce(function(value) {
			// const payload = value === null || value === "" ? null : value
			this.searchTerm = value
			this.$store.commit("caseManagement/SET_PAGE", 1)
			this.readAll()
		}, 200),

		cancelSearch() {
			this.searchTerm = null
			this.$store.commit("caseManagement/SET_PAGE", 1)
			this.onSearch()
		},

		async readAll() {
			const query = gql`
				query caseManagements(
					$limit: Int
					$sort: sortInput
					$skip: Int
					$where: caseWhere
				) {
					cases(limit: $limit, sort: $sort, where: $where, skip: $skip) {
						totalCount
						list {
							id
							caseNumber
							createdAt
							component {
								id
								name
							}
							holder {
								name
							}
						}
					}
				}
			`

			// Sorting - sort by createdAt
			let sort = { fieldName: "createdAt", type: "desc" }

			// Pagination
			const skip = (this.computedPage - 1) * this.itemsPerPage
			const limit = this.itemsPerPage

			// Search
			const where = this.computedWhere

			this.isLoading = true
			const res = await api({
				query,
				variables: {
					sort,
					skip,
					limit,
					where
				}
			})
			this.isLoading = false

			// Check errors
			if (res.errors)
				this.$store.dispatch("showError", [res.errors?.[0]?.message])

			this.listing.list = res?.data?.cases?.list || []
			this.listing.numberOfItems = res?.data?.cases?.totalCount || 0
		},

		showModal(id) {
			this.selectedItemId = id
			this.$modal.show("j-dialog")
		},

		hideModal() {
			this.selectedItemId = null
			this.$modal.hide("j-dialog")
		},

		//TODO: Only delete when no product/policy attached to productType
		async removeCase(id) {
			const query = gql`
				mutation deleteCase($id: String!) {
					deleteCase(id: $id) {
						status
						errors
						errors_2 {
							code
							message
						}
					}
				}
			`

			const res = await api({ query, variables: { id } })

			this.hideModal()

			if (res?.data?.deleteCase?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.deleteCase?.errors ||
							res?.data?.deleteCase?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("case_deleted")
					}
				})

			this.readAll()
		}
	},

	beforeRouteLeave(to, from, next) {
		// If in same module , do nothing, do not reset page
		if (to.meta.featureId === from.meta.featureId) {
			next()
		} else {
			// if not in same module, reset page to 1
			this.$store.commit("caseManagement/SET_PAGE", 1)
			next()
		}
	},

	mounted() {
		this.readAll()
	}
}
</script>
